import { inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { forkJoin, Observable, of, throwError } from 'rxjs';
import { map, mapTo, retry, switchMap, take } from 'rxjs/operators';

import { BaseRecordsSandbox } from '@common/containers/base-record/base-record-container.container';
import { UploadHelper } from '@common/sandboxes/upload/upload-helper.sandbox';
import { RoutingService } from '@common/services/routing/routing.service';

import { DatePipe } from '@angular/common';
import {
    BaseSandbox,
    ClassificationService,
    DEFAULT_TRIES,
    ExportJob,
    ExportsService,
    RecordHolderService,
    RecordsService
} from '@mediahaven/shared';
import {
    ActiveElementReference,
    ApiFragment,
    ApiQuery,
    ApiQueryBuilder,
    ApiRecordType,
    BatchActions,
    BatchObject,
    BatchSearchRequest,
    BatchService,
    BatchStatus,
    BulkSearchRequest,
    Combine,
    ExtendedProfile,
    ExtendedProfileField,
    ExtendedProfileService,
    FacialRecognitionConsent,
    Family,
    FragmentId,
    GenerativeMetadataGuard,
    Identifier,
    MediaObjectId,
    mergeValidationResponses,
    PartialRecord,
    Profile,
    ProfileDataService,
    ProfileField,
    PublicationPolicy,
    QueryPart,
    QueryStateHandler,
    RecordId,
    RecordInheritance,
    RecordInheritanceDataService,
    RecordStatus,
    RecordType,
    RecordTypesService,
    Restriction,
    SearchRequest,
    User,
    ValidationResponse,
    ZoneRestrictions
} from '@stylehaven/components';
import { flatMap } from 'lodash-es';
import { RecordChildrenSandbox } from './record-children.sandbox';

@Injectable({ providedIn: 'root' })
export class RecordsSandbox extends BaseSandbox implements BaseRecordsSandbox {
    private readonly recordInheritanceDataService: RecordInheritanceDataService = inject(RecordInheritanceDataService);
    private readonly recordHolderService: RecordHolderService = inject(RecordHolderService);

    private readonly generativeMetadataGuard: GenerativeMetadataGuard = inject(GenerativeMetadataGuard);
    private readonly profileDataService: ProfileDataService = inject(ProfileDataService);
    private readonly recordTypesService: RecordTypesService = inject(RecordTypesService);
    private readonly recordChildrenSandbox: RecordChildrenSandbox = inject(RecordChildrenSandbox);

    constructor(
        private readonly recordsService: RecordsService,
        private readonly batchService: BatchService,
        private readonly classificationService: ClassificationService,
        private readonly profileService: ExtendedProfileService,
        private readonly routingService: RoutingService,
        private readonly zoneRestrictions: ZoneRestrictions,
        private readonly queryStateHandler: QueryStateHandler,
        private readonly uploadHelper: UploadHelper,
        private readonly exportsService: ExportsService,
        private readonly datePipe: DatePipe
    ) {
        super();
    }

    // #region Record Getters

    public getFullRecord(limitedRecord: ApiFragment): Observable<ApiFragment> {
        return this.recordHolderService.getRecord(limitedRecord.getFragmentId());
    }

    public getActiveRecord(queryIteratorId: string): Observable<ActiveElementReference | undefined> {
        return this.queryStateHandler.getActive(queryIteratorId);
    }

    public setActiveRecord(queryIteratorId: string, identifier: FragmentId): void {
        try {
            this.queryStateHandler.setActive(queryIteratorId, identifier);
        } catch (error) {
            // Ignore failures
        }
    }

    public getRecord(recordId: FragmentId): Observable<ApiFragment> {
        return this.recordsService.getRecord(recordId);
    }

    public getRecordExif(recordId: FragmentId): Observable<Record<string, string>> {
        return this.recordsService.getExif(recordId);
    }

    public getRecords(queryParts: QueryPart[], restriction: Restriction): Observable<ApiFragment[]> {
        return this.recordsService.getAllRecords(new ApiQuery(queryParts, restriction), { annotate: false });
    }

    public getAllRecords(queryParts: QueryPart[], restriction: Restriction): Observable<ApiFragment[]> {
        const restrictions: Restriction = Combine.And(this.zoneRestrictions.allNonDeletedRecordsZone(), restriction);
        return this.recordsService.getAllRecords(new ApiQuery(queryParts, restrictions));
    }

    public getRecordDirectParent(record: ApiFragment): Observable<ApiFragment | undefined> {
        let parentId: Identifier | null = record.getParentRecordId();

        if (record.isCandidateChild()) {
            parentId = record.getCandidateParentId();
        }

        if (!parentId) {
            return of(undefined);
        }

        return this.recordsService.getRecord(parentId);
    }

    public getRecordClassification(record: ApiFragment): Observable<ApiFragment | undefined> {
        const classificationId: MediaObjectId | null = record.getClassificationId();

        if (!classificationId) {
            return of(undefined);
        }

        return this.classificationService.get(classificationId);
    }

    public getInvalidChildIds(parentId: RecordId): Observable<RecordId[]> {
        return this.recordsService.getChildIds(parentId, [
            RecordStatus.DraftInvalid,
            RecordStatus.Processing,
            RecordStatus.Rejected
        ]);
    }

    public getAllRecordsZone(): Restriction {
        return this.zoneRestrictions.allNonDeletedRecordsZone();
    }

    public getRecordInheritance(recordId: Identifier): Observable<RecordInheritance> {
        return this.recordInheritanceDataService.getInheritance(recordId);
    }

    public getAiProfile(): Observable<Profile | undefined> {
        const aiProfileStream$: Observable<Profile> = this.getCurrentUser().pipe(
            switchMap(user => this.profileDataService.getAiProfile(user.Organisation.Id))
        );

        return this.generativeMetadataGuard
            .hasAccess()
            .pipe(switchMap(hasAccess => (hasAccess ? aiProfileStream$ : of(undefined))));
    }
    // #endregion

    // #region Record Types
    public hasCascadingPublicationPolicy(recordType: RecordType): boolean {
        return this.recordTypesService.hasPublicationPolicy(recordType, PublicationPolicy.Cascade);
    }
    // #endregion

    // #region Save Records
    public saveRecord(fragmentId: FragmentId, changes: PartialRecord[]): Observable<ApiFragment> {
        return this.recordsService.saveRecord(fragmentId, changes);
    }

    public saveRecords(
        fragments: readonly ApiFragment[],
        changes: PartialRecord[],
        batchTag?: string
    ): Observable<BatchObject> {
        const searchRequest: BulkSearchRequest = new BulkSearchRequest(fragments, this.getAllRecordsZone());
        return this.batchService.sendBatch(BatchActions.MERGE_MULTIPLE, {
            changes,
            searchRequest,
            waitForFinish: true,
            isBulk: true,
            batchTag: batchTag
        });
    }

    public batchSaveRecords(query: ApiQuery, changes: PartialRecord[], batchTag?: string): Observable<BatchObject> {
        const luceneQueryString: string = ApiQueryBuilder.queryToLuceneQuery(query);
        const searchRequest: SearchRequest = { Query: luceneQueryString };
        return this.batchService.sendBatch(BatchActions.MERGE_MULTIPLE, {
            changes,
            searchRequest,
            isBulk: false,
            batchTag: batchTag
        });
    }

    public checkMinimalRequiredFields(records: readonly ApiFragment[]): Observable<ValidationResponse> {
        return forkJoin(records.map(record => this.recordsService.checkRecordValidity(record))).pipe(
            map(validations => mergeValidationResponses(validations))
        );
    }

    public createRelation(
        record: ApiFragment,
        relationFragmentId: FragmentId,
        relationType: string
    ): Observable<ApiFragment> {
        const recordRelations: FragmentId[] = record.getRelationFragmentIdsOfType(relationType);
        recordRelations.push(relationFragmentId);

        const metadata: PartialRecord = new PartialRecord();
        metadata.set(Family.Structural, 'Relations', { [relationType]: recordRelations });

        return this.recordsService.saveRecord(record.getFragmentId(), [metadata]);
    }

    // #endregion

    // #region Delete Records
    public deleteRecord(record: ApiFragment, user: User): Observable<boolean> {
        return this.recordsService.deleteRecord(record);
    }

    public bulkDeleteRecords(records: readonly ApiFragment[]): Observable<boolean> {
        const searchRequest: BulkSearchRequest = new BulkSearchRequest(records, this.getAllRecordsZone());

        return this.batchService
            .sendBatch(BatchActions.DELETE, {
                searchRequest: searchRequest,
                waitForFinish: true,
                isBulk: false,
                batchTag: 'Bulk delete'
            })
            .pipe(map(batchResponse => this.batchResponseToStatus(batchResponse)));
    }

    public permanentlyDeleteRecord(record: ApiFragment): Observable<boolean> {
        return this.recordsService.permanentlyDeleteRecord(record);
    }

    public bulkPermanentDeleteRecords(records: readonly ApiFragment[]): Observable<boolean[]> {
        return this.recordsService.bulkPermanentlyDeleteRecords(records);
    }

    // #endregion

    // #region Publish Records
    public publishRecord(record: ApiFragment): Observable<boolean> {
        return this.recordsService.publishRecord(record);
    }

    public batchPublish(query: ApiQuery, batchTag?: string): Observable<boolean> {
        const searchRequest: BatchSearchRequest = new BatchSearchRequest(query);

        return this.batchService
            .sendBatch(BatchActions.PUBLISH, {
                searchRequest: searchRequest,
                waitForFinish: false,
                isBulk: false,
                batchTag
            })
            .pipe(mapTo(true));
    }

    // #endregion

    // #region Recover Records
    public recoverDeletedRecord(record: ApiFragment): Observable<ApiFragment> {
        return this.recordsService.recoverDeletedRecord(record);
    }

    public bulkRecoverDeletedRecords(records: readonly ApiFragment[]): Observable<ApiFragment[]> {
        return this.recordsService.bulkRecoverDeletedRecords(records);
    }

    // #endregion

    // #region Adding and uploading to intellectual
    public uploadChildRecords(
        parent: ApiFragment,
        config: {
            zoneId: string;
            recordType: ApiRecordType;
            fileList?: FileList;
            facialRecognitionConsent?: FacialRecognitionConsent;
        }
    ): Observable<boolean> {
        const isPublished: boolean = parent.isPublished();

        const fileUpload$: Observable<FileList> = config.fileList ? of(config.fileList) : this.uploadHelper.askUserForFiles();

        return fileUpload$.pipe(
            switchMap(files =>
                isPublished
                    ? this.uploadToPublishedParent(parent, { files, ...config })
                    : this.uploadToConceptParent(parent, { files, ...config })
            ),
            // Wait until at least 1 of the children is indexed so Content tab displays
            switchMap(() =>
                this.recordChildrenSandbox.hasChildren(parent.getRecordId(), parent.getFragmentId()).pipe(
                    switchMap(hasChildren =>
                        hasChildren ? of(hasChildren) : throwError(() => 'Children not indexed yet')
                    ),
                    retry(DEFAULT_TRIES)
                )
            )
        );
    }

    // MED-2079 + MED-2206:
    // If not published or not an Mh2Collection upload directly as child of intellectual
    private uploadToPublishedParent(
        parent: ApiFragment,
        config: {
            files: FileList;
            zoneId: string;
            recordType: ApiRecordType;
            facialRecognitionConsent?: FacialRecognitionConsent;
        }
    ): Observable<boolean> {
        return this.uploadHelper
            .uploadFiles(config.files, {
                parentRecordId: parent.getMediaObjectId(),
                zoneId: config.zoneId,
                recordType: config.recordType,
                facialRecognitionConsent: config.facialRecognitionConsent
            })
            .pipe(map(() => true));
    }

    private uploadToConceptParent(
        parent: ApiFragment,
        config: {
            files: FileList;
            zoneId: string;
            recordType: ApiRecordType;
            facialRecognitionConsent?: FacialRecognitionConsent;
        }
    ): Observable<boolean> {
        const parentId: MediaObjectId = parent.getMediaObjectId()!;

        return this.uploadHelper
            .uploadFiles(config.files, {
                parentRecordId: parentId,
                zoneId: config.zoneId,
                recordType: config.recordType,
                facialRecognitionConsent: config.facialRecognitionConsent
            })
            .pipe(map(() => true));
    }

    public addRecordToParent(record: ApiFragment, parent: ApiFragment): Observable<ApiFragment> {
        return this.recordsService.addRecordToParent(record, parent);
    }

    public bulkCoupleRecordsToParent(records: readonly ApiFragment[], parent: ApiFragment): Observable<boolean> {
        const hasConcepts: boolean = records.some(record => record.isConcept());
        const isPublishedIntellectual: boolean = parent.isPublished();

        // MED-1995: Concepts should be adopted as candidate-child by published intellectuals
        // Batch does not have this option
        if (hasConcepts && isPublishedIntellectual) {
            return this.recordsService.addRecordsToParent(records, parent).pipe(mapTo(true));
        }

        const searchRequest: BulkSearchRequest = new BulkSearchRequest(records, this.getAllRecordsZone());
        return this.batchService
            .sendBatch(BatchActions.COUPLE_TO_PARENT, {
                parent,
                searchRequest,
                waitForFinish: true,
                isBulk: true
            })
            .pipe(mapTo(true));
    }

    public batchCoupleRecordsToParent(query: ApiQuery, parent: ApiFragment, batchTag?: string): Observable<boolean> {
        const searchRequest: BatchSearchRequest = new BatchSearchRequest(query);
        return this.batchService
            .sendBatch(BatchActions.COUPLE_TO_PARENT, {
                fragments: [],
                parent,
                searchRequest,
                isBulk: false,
                batchTag
            })
            .pipe(mapTo(true));
    }

    public batchStartCSVBatch(query: ApiQuery, profileId: string, batchTag?: string): Observable<boolean> {
        const exportFileName: string = `Metadata-export-${this.datePipe.transform(new Date(), 'yyyyMMddHHmmss')}.csv`;

        const searchRequest: BatchSearchRequest = new BatchSearchRequest(query);
        return this.batchService
            .sendBatch(BatchActions.CSV_EXPORT, {
                fragments: [],
                searchRequest,
                isBulk: false,
                profileId,
                exportFileName,
                waitForFinish: true,
                batchTag
            })
            .pipe(mapTo(true));
    }

    // #endregion

    // #region Remove from parent
    public removeRecordFromParent(record: ApiFragment, parent: ApiFragment): Observable<ApiFragment> {
        return this.recordsService.removeRecordFromParent(record, parent.getMediaObjectId());
    }

    public bulkRemoveRecordsFromParent(fragments: readonly ApiFragment[]): Observable<BatchObject> {
        const searchRequest: BulkSearchRequest = new BulkSearchRequest(fragments, this.getAllRecordsZone());
        return this.batchService.sendBatch(BatchActions.REMOVE_FROM_INTELLECTUAL, {
            fragments,
            searchRequest,
            waitForFinish: true,
            isBulk: true
        });
    }

    // #endregion

    // #region Export Records
    public getExportJob(exportJobId: string): Observable<ExportJob> {
        return this.exportsService.getExportJob(exportJobId);
    }

    public getDownloadStatus(exportJobId: string): Observable<ExportJob> {
        return this.exportsService.getDownloadStatus(exportJobId);
    }

    // #endregion

    // #region Profiles and Classifications
    public getProfiles(profileIds: string[]): Observable<ExtendedProfile[]> {
        return this.profileService.getExtended(profileIds);
    }

    public getClassificationByTitle(classificationTitle: string): Observable<ApiFragment | undefined> {
        return this.classificationService.getSingleByTitle(classificationTitle);
    }

    public translateDottedKeysToProfileLabels(profileIds: string[], dottedKeys: string[]): Observable<string[]> {
        const extendedProfileFields$: Observable<(ProfileField | ExtendedProfileField)[]> = this.profileService
            .getExtended(profileIds)
            .pipe(
                take(1),
                map(profiles => flatMap(profiles.map(profile => profile.Fields)))
            );

        return extendedProfileFields$.pipe(
            map(profileFields => dottedKeys.map(key => this.findProfileLabelByDottedKey(profileFields, key)))
        );
    }

    private findProfileLabelByDottedKey(
        profileFields: (ProfileField | ExtendedProfileField)[],
        dottedKey: string
    ): string {
        const profileField: ExtendedProfileField | undefined = profileFields
            .filter((field: ProfileField): field is ExtendedProfileField => field.hasOwnProperty('FieldDefinition'))
            .find(field => field.FieldDefinition.DottedKey === dottedKey);

        return profileField?.DisplayLabel ? profileField.DisplayLabel : dottedKey;
    }

    // #endregion

    // #region Utils
    private batchResponseToStatus(batchResponse: BatchObject): boolean {
        const failedStatusses: BatchStatus[] = [
            BatchStatus.TOO_MANY_FAILED,
            BatchStatus.COMPLETED_WITH_ERRORS,
            BatchStatus.CANCELED
        ];

        return !failedStatusses.includes(batchResponse.Status);
    }

    public navigateBack(activatedRoute: ActivatedRoute): void {
        this.routingService.navigateToPreviousContext(['../'], { relativeTo: activatedRoute });
    }

    // #endregion
}
